import { useState } from 'react';
import { toast } from 'sonner';
import { 
  validateSingleFacturation, 
  paySingleCommission,
  payFinalFacturation 
} from '../../service/api/facturationService';
import { formatCurrencys, handleApiError } from '../utils/facturationUtils';



/**
 * HOOK POUR LES ACTIONS DE FACTURATION
 * Centralise la logique des actions avec gestion d'erreurs robuste
 */
export const useFacturationActions = (onRefresh) => {
  const [loadingActions, setLoadingActions] = useState({});

  const [confirmationModal, setConfirmationModal] = useState({
    show: false,
    type: null,
    facturation: null,
    message: '',
    onConfirm: null
  });

  /**
   * Ouvrir le modal de confirmation
   */
  const showConfirmation = (type, facturation, message, onConfirmAction) => {
    setConfirmationModal({
      show: true,
      type,
      facturation,
      message,
      onConfirm: onConfirmAction
    });
  };

  /**
   * Fermer le modal
   */
  const hideConfirmation = () => {
    setConfirmationModal({
      show: false,
      type: null,
      facturation: null,
      message: '',
      onConfirm: null
    });
  };

  /**
   * Exécuter l'action après confirmation
   */
  const executeAction = async (facturation, actionType, actionFunction) => {
    setLoadingActions(prev => ({ ...prev, [`${actionType}-${facturation.id}`]: true }));
    
    try {
      await actionFunction();
      toast.success(getSuccessMessage(actionType));
      onRefresh?.();
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast.error(`❌ ${getErrorMessage(actionType)}: ${errorMessage}`);
    } finally {
      setLoadingActions(prev => ({ ...prev, [`${actionType}-${facturation.id}`]: false }));
    }
  };

  /**
   * Valider une facturation (avec confirmation)
   */
  const handleValidate = (facturation) => {
    if (facturation.facturationStatut === 1) {
      toast.info('✅ Facturation déjà validée');
      return;
    }

    showConfirmation(
      'validate',
      facturation,
      `Êtes-vous sûr de vouloir valider la facturation #${facturation.id} ?\n\nClient: ${facturation.accountName}\nTotal Auc: ${formatCurrencys(facturation.totalAuc)} Fcfa`,
      () => executeAction(
        facturation, 
        'validate', 
        () => validateSingleFacturation(facturation.id)
      )
    );
  };

  /**
   * Payer la commission (avec confirmation)
   */
  const handlePayCommission = (facturation) => {
    
    if (facturation.isFinalPaid) {
      toast.info('💰 Commission déjà payée');
      return;
    }

    if (facturation.facturationStatut !== 1) {
      toast.warning('⚠️ La facturation doit être validée avant de payer la commission');
      return;
    }

    const commissionAmount = facturation.totalAuc * (facturation.commissions / 100);

    showConfirmation(
      'commission',
      facturation,
      `Êtes-vous sûr de vouloir payer la commission ?\n\nClient: ${facturation.accountName}\nCommission: ${formatCurrencys(commissionAmount)}  (${facturation.commissions}%)`,
      () => executeAction(
        facturation,
        'commission',
        () => paySingleCommission(facturation.id)
      )
    );
  };

  /**
   * Paiement final (troisième étape)
   */
  const handleFinalPayment = (facturation) => {
    if (facturation.hasPaid) {
      toast.info('✅ Facturation déjà payée');
      return;
    }

    if (!facturation.commissionPaid) {
      toast.warning('💰 La commission doit être payée avant le paiement final');
      return;
    }

    if (facturation.facturationStatut !== 1) {
      toast.warning('⚠️ La facturation doit être validée');
      return;
    }

    const totalAmount = facturation.totalAuc * (facturation.commissions / 100);

    showConfirmation(
      'final',
      facturation,
      `Êtes-vous sûr de vouloir effectuer le paiement final ?\n\nClient: ${facturation.accountName}\nMontant total: ${formatCurrencys(totalAmount)} Fcfa\n\nCette action est irréversible.`,
      () => executeAction(
        facturation,
        'final',
        () => payFinalFacturation(facturation.id)
      )
    );
  };

  /**
   * Messages de succès/erreur
   */
  const getSuccessMessage = (actionType) => {
    const messages = {
      validate: '🎉 Facturation validée avec succès !',
      commission: '💳 Commission payée avec succès !',
      final: '🎊 Paiement final effectué avec succès !'
    };
    return messages[actionType] || 'Action réalisée avec succès';
  };

  const getErrorMessage = (actionType) => {
    const messages = {
      validate: 'Échec de la validation',
      commission: 'Échec du paiement commission',
      final: 'Échec du paiement final'
    };
    return messages[actionType] || 'Échec de l\'action';
  };


  /**
   * Vérifie si une action est en cours
   */
  const isLoading = (facturationId, actionType) => {
    return loadingActions[`${actionType}-${facturationId}`] || false;
  };

  return {
    handleValidate,
    handlePayCommission,
    handleFinalPayment,
    isLoading,
    confirmationModal,
    hideConfirmation  
  };
};